import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  StatTile,
  type BadgeTone,
  type Column,
  useToast,
} from '@/components/ui';
import {
  type AgeingResponse,
  type ClaimBatch,
  type InvoiceSchedule,
  type MemberVerification,
  type Remittance,
  useFinanceActions,
  useInvoices,
  useMemberVerifications,
  usePanels,
  useRemittances,
} from '@/features/admin-v2/api';
import {
  DEDUCTION_DECISIONS,
  deductionDecisionLabel,
} from '@/features/admin-v2/claim-status';
import { downloadFile } from '@/features/admin-v2/download';
import { ReportDispatchPanel } from '@/pages/ops/ReportDispatch';
import { CreditNoteDialog } from './CreditNoteDialog';
import { useMe, useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { formatRM, parseRM } from '@/features/billing/money';
import { api, isApiError } from '@/lib/api';
import { todayKl } from '@/pages/calendar/time';
import dash from '@/pages/dashboards/dashboards.module.css';
import { dayLabel } from '@/pages/ops/register-format';
import styles from '@/pages/queue/queue.module.css';
import local from './finance.module.css';

const BATCH: Record<string, { label: string; tone: BadgeTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  review: { label: 'For review', tone: 'warning' },
  issued: { label: 'Submitted', tone: 'warning' },
  part_paid: { label: 'Partially paid', tone: 'warning' },
  paid: { label: 'Paid', tone: 'success' },
  overdue: { label: 'Overdue', tone: 'danger' },
};
const batchStatus = (s: string) =>
  BATCH[s] ?? { label: s, tone: 'neutral' as BadgeTone };

function useFail() {
  const toast = useToast();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not done',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

const RUN_RESULT: Record<string, { label: string; tone: BadgeTone }> = {
  reminder: { label: 'Reminder', tone: 'info' },
  nil: { label: 'Nothing to bill', tone: 'neutral' },
  draft: { label: 'Draft to review', tone: 'warning' },
  issued: { label: 'Issued', tone: 'success' },
};

/**
 * RL-1776: the "demo clock" runs the monthly invoicing as if it were another
 * date. A testing aid: shown on staging (and local/test) only.
 */
function DemoClock() {
  const me = useMe();
  const { hasPermission } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [date, setDate] = useState(todayKl());
  const run = useMutation({
    mutationFn: () =>
      api.post<{ date: string; results: unknown[] }>(
        '/finance/schedules/demo-clock',
        { date },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance'] }),
  });
  if (
    !['staging', 'local', 'test'].includes(me.appEnv) ||
    !hasPermission('invoices:manage')
  )
    return null;
  return (
    <span className={local.acts}>
      <Input
        type="date"
        aria-label="Demo clock date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      <Button
        size="sm"
        loading={run.isPending}
        onClick={() =>
          void run
            .mutateAsync()
            .then((r) =>
              toast.show({
                tone: 'success',
                title: `Ran ${dayLabel(r.data.date)} (demo clock)`,
                body: r.data.results.length
                  ? `${r.data.results.length} panel(s) ran.`
                  : 'Nothing was due on that day.',
              }),
            )
            .catch(fail)
        }
      >
        Run as this date
      </Button>
    </span>
  );
}

/** "Monthly invoices" (prototype invoicing(): schedule per panel, then the batches). */
export function Invoices() {
  const { hasPermission } = useSession();
  const schedules = useQuery({
    queryKey: ['finance', 'schedules'],
    queryFn: ({ signal }) =>
      api
        .get<InvoiceSchedule[]>('/finance/schedules', { signal })
        .then((r) => r.data),
    enabled: hasPermission('invoices:read'),
  });
  const invoices = useInvoices();
  const actions = useFinanceActions();
  const toast = useToast();
  const fail = useFail();
  const canIssue = hasPermission('claims:manage');
  const [crediting, setCrediting] = useState<ClaimBatch | null>(null);
  const scheduleCols: Column<InvoiceSchedule>[] = [
    { key: 'p', header: 'Panel', render: (s) => <strong>{s.name}</strong> },
    {
      key: 'd',
      header: 'Invoice day',
      render: (s) =>
        s.invoiceDay === null ? (
          <span className={styles.sub}>Not set</span>
        ) : (
          `${s.invoiceDay === 31 ? 'Last day' : `Day ${s.invoiceDay}`} of each month`
        ),
    },
    {
      key: 'r',
      header: 'Reminders',
      render: (s) => `${s.reminderDaysBefore} days before`,
    },
    {
      key: 'm',
      header: 'After building',
      render: (s) => (
        <Badge tone={s.invoiceMode === 'auto' ? 'info' : 'warning'}>
          {s.invoiceMode === 'auto' ? 'Sends automatically' : 'Review first'}
        </Badge>
      ),
    },
    {
      key: 'n',
      header: 'Next run',
      render: (s) => (s.nextRun ? dayLabel(s.nextRun) : '-'),
    },
    {
      key: 'nr',
      header: 'Next reminder',
      render: (s) => (s.nextReminder ? dayLabel(s.nextReminder) : '-'),
    },
  ];
  // RL-1776 (gap #100): the monthly runs and reminders, newest first.
  const runs = (schedules.data ?? [])
    .flatMap((s) => s.runs.map((r) => ({ ...r, panel: s.name })))
    .sort((a, b) => b.at.localeCompare(a.at));
  const batchCols: Column<ClaimBatch>[] = [
    {
      key: 'n',
      header: 'Invoice',
      render: (b) => (
        <>
          <span className="mono">{b.number}</span>
          <p className={styles.sub}>{dayLabel(b.createdAt)}</p>
        </>
      ),
    },
    {
      key: 'p',
      header: 'Panel · branch',
      render: (b) => (
        <>
          {b.payer.name}
          <p className={styles.sub}>{b.branch.name}</p>
        </>
      ),
    },
    { key: 'c', header: 'Claims', align: 'right', render: (b) => b.claims },
    {
      key: 'i',
      header: 'Invoiced',
      align: 'right',
      render: (b) => formatRM(b.totalSen),
    },
    {
      key: 'o',
      header: 'Open',
      align: 'right',
      render: (b) => formatRM(b.outstandingSen ?? b.totalSen - b.paidSen),
    },
    {
      key: 's',
      header: 'Status',
      render: (b) => {
        const s = batchStatus(b.status);
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
    {
      key: 'a',
      header: <span className="visually-hidden">Actions</span>,
      align: 'right',
      render: (b) => (
        <span className={local.acts}>
          <Button
            size="sm"
            icon={<Download size={16} />}
            onClick={() =>
              void downloadFile(
                `/finance/invoices/${b.id}/csv`,
                `${b.number}.csv`,
              ).catch(fail)
            }
          >
            CSV
          </Button>
          {canIssue && ['issued', 'part_paid'].includes(b.status) && (
            <Button size="sm" onClick={() => setCrediting(b)}>
              Credit note
            </Button>
          )}
          {canIssue && ['draft', 'review'].includes(b.status) && (
            <Button
              size="sm"
              variant="primary"
              loading={actions.issueBatch.isPending}
              onClick={() =>
                void actions.issueBatch
                  .mutateAsync({ id: b.id, method: 'export' })
                  .then(() =>
                    toast.show({ tone: 'success', title: 'Invoice issued' }),
                  )
                  .catch(fail)
              }
            >
              Issue
            </Button>
          )}
        </span>
      ),
    },
  ];
  return (
    <>
      <p className={local.note}>
        Each panel's invoice builds itself on its invoice day: every validated
        claim up to that day becomes one invoice per clinic code. Panels set to
        Review first wait as a draft for Finance to approve.
      </p>
      <Card
        title="Invoice schedule"
        flush
        actions={
          <span className={styles.sub}>{`Today ${dayLabel(todayKl())}`}</span>
        }
      >
        <DataTable
          caption="Invoice schedule"
          rows={schedules.data}
          rowKey={(s) => s.id}
          loading={schedules.isLoading}
          error={schedules.error}
          columns={scheduleCols}
          empty={<EmptyState title="No panels" />}
        />
      </Card>
      <Card title="Monthly runs and reminders" flush actions={<DemoClock />}>
        <DataTable
          caption="Monthly runs and reminders"
          rows={schedules.data ? runs : undefined}
          rowKey={(r) => r.id ?? `${r.panel}-${r.at}-${r.result}`}
          loading={schedules.isLoading}
          error={schedules.error}
          columns={[
            {
              key: 'at',
              header: 'Date',
              render: (r) => dayLabel(r.at),
            },
            { key: 'p', header: 'Panel', render: (r) => r.panel },
            {
              key: 'r',
              header: 'Result',
              render: (r) => (
                <span className={local.badges}>
                  <Badge tone={RUN_RESULT[r.result]?.tone ?? 'neutral'}>
                    {RUN_RESULT[r.result]?.label ?? r.result}
                  </Badge>
                  {r.simulatedClock && <Badge tone="neutral">Demo clock</Badge>}
                </span>
              ),
            },
            {
              key: 'c',
              header: 'Claims',
              align: 'right',
              render: (r) => r.claims,
            },
            { key: 'n', header: 'Note', render: (r) => r.note ?? '-' },
          ]}
          empty={
            <EmptyState
              title="No runs yet"
              body="Each panel's invoice runs on its invoice day; a reminder comes first."
            />
          }
        />
      </Card>
      <Card
        title="Invoices"
        flush
        actions={<span className={styles.sub}>Newest first</span>}
      >
        <DataTable
          caption="Invoices"
          rows={invoices.data}
          rowKey={(b) => b.id}
          loading={invoices.isLoading}
          error={invoices.error}
          onRetry={() => void invoices.refetch()}
          columns={batchCols}
          empty={
            <EmptyState
              title="No invoices yet"
              body="Create a batch from Ready claims."
            />
          }
        />
      </Card>
      {crediting && (
        <CreditNoteDialog
          invoice={crediting}
          onClose={() => setCrediting(null)}
        />
      )}
    </>
  );
}

/** "Outstanding / ageing" (prototype aging(): bucket tiles, outstanding batches, statements). */
export function Ageing() {
  const { selected } = useBranch();
  const fail = useFail();
  const q = useQuery({
    queryKey: ['finance', 'ageing-batches', selected?.id ?? 'all'],
    queryFn: ({ signal }) =>
      api
        .get<AgeingResponse>('/finance/ageing', { signal })
        .then((r) => r.data),
  });
  const [statementFor, setStatementFor] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const bucket = (b: string) =>
    q.data?.buckets.find((x) => x.bucket === b)?.sen ?? 0;
  const late = (b: string) =>
    (q.data?.rows ?? []).filter((r) => r.bucket === b && r.overdue);
  const payers = [
    ...new Map((q.data?.rows ?? []).map((r) => [r.payer.id, r.payer])).values(),
  ];
  return (
    <>
      <div className={dash.tiles}>
        {['0-30', '31-60', '61-90', '90+'].map((b) => (
          <StatTile
            key={b}
            label={`${b} days`}
            value={q.data ? formatRM(bucket(b)) : '—'}
            caption={
              late(b).length ? (
                <span className={local.late}>
                  {late(b)
                    .map((r) => `${r.payer.name} · ${r.ageDays} d`)
                    .join(', ')}{' '}
                  past terms
                </span>
              ) : undefined
            }
          />
        ))}
      </div>
      <Card
        title="Outstanding batches"
        flush
        actions={
          <>
            <Button
              size="sm"
              icon={<Download size={16} />}
              onClick={() =>
                void downloadFile(
                  '/finance/ageing/csv',
                  `ageing-${todayKl()}.csv`,
                ).catch(fail)
              }
            >
              Export CSV
            </Button>
            {payers.length > 0 && (
              <Select
                aria-label="Statement of account"
                className={local.inlineSelect}
                value=""
                placeholder="Statement of account…"
                options={payers.map((p) => ({ value: p.id, label: p.name }))}
                onChange={(e) => {
                  const p = payers.find((x) => x.id === e.target.value);
                  if (p) setStatementFor({ id: p.id, name: p.name });
                }}
              />
            )}
          </>
        }
      >
        <DataTable
          caption="Outstanding batches"
          rows={q.data?.rows}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          empty={<EmptyState title="Nothing outstanding" />}
          columns={[
            {
              key: 'n',
              header: 'Batch',
              render: (r) => <span className="mono">{r.number}</span>,
            },
            { key: 'p', header: 'Panel', render: (r) => r.payer.name },
            {
              key: 'o',
              header: 'Open',
              align: 'right',
              render: (r) => formatRM(r.outstandingSen),
            },
            {
              key: 's',
              header: 'Submitted',
              render: (r) => (r.issuedAt ? dayLabel(r.issuedAt) : '-'),
            },
            {
              key: 'a',
              header: 'Age',
              render: (r) => (
                <Badge tone={r.overdue ? 'danger' : 'neutral'}>
                  {r.overdue ? `Overdue · ${r.ageDays} d` : `${r.ageDays} d`}
                </Badge>
              ),
            },
            {
              key: 'x',
              header: 'Next action',
              render: (r) =>
                r.nextAction ? `Follow up ${dayLabel(r.nextAction)}` : '-',
            },
          ]}
        />
      </Card>
      <ReportDispatchPanel report="panel_statement" />
      {statementFor && (
        <StatementDrawer
          payer={statementFor}
          onClose={() => setStatementFor(null)}
        />
      )}
    </>
  );
}

interface Statement {
  payer: { id: string; name: string; contactEmail: string | null };
  asAt: string;
  rows: AgeingResponse['rows'];
  totalSen: number;
}

function StatementDrawer({
  payer,
  onClose,
}: {
  payer: { id: string; name: string };
  onClose: () => void;
}) {
  const { hasPermission } = useSession();
  const toast = useToast();
  const fail = useFail();
  const q = useQuery({
    queryKey: ['finance', 'statement', payer.id],
    queryFn: ({ signal }) =>
      api
        .get<Statement>(`/finance/statements/${payer.id}`, { signal })
        .then((r) => r.data),
  });
  const send = useMutation({
    mutationFn: () =>
      api.post<{
        sent: boolean;
        dispatch: { status: string; reason: string | null };
      }>(`/finance/statements/${payer.id}/send`),
  });
  const s = q.data;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Statement of account · ${payer.name}`}
      footer={
        hasPermission('claims:manage') && (
          <Button
            variant="primary"
            loading={send.isPending}
            disabled={!s?.payer.contactEmail}
            onClick={() =>
              void send
                .mutateAsync()
                .then((r) =>
                  // RL-1033: never claim "sent" without a delivery provider.
                  toast.show(
                    r.data.sent
                      ? { tone: 'success', title: 'Statement sent' }
                      : {
                          tone: 'info',
                          title: 'Not sent: recorded as unavailable',
                          body: r.data.dispatch.reason ?? undefined,
                        },
                  ),
                )
                .catch(fail)
            }
          >
            Send statement
          </Button>
        )
      }
    >
      {s && (
        <div className={local.form}>
          <dl className={local.kv}>
            <dt>As at</dt>
            <dd>{dayLabel(s.asAt)}</dd>
            <dt>Send to</dt>
            <dd>{s.payer.contactEmail ?? 'No contact email on the panel'}</dd>
            <dt>Total open</dt>
            <dd>{formatRM(s.totalSen)}</dd>
          </dl>
          <table className={dash.table}>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Age</th>
                <th>Open</th>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r) => (
                <tr key={r.id}>
                  <td className="mono">{r.number}</td>
                  <td>{`${r.ageDays} d`}</td>
                  <td>{formatRM(r.outstandingSen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}

const REMIT: Record<string, { label: string; tone: BadgeTone }> = {
  unapplied: { label: 'Unapplied', tone: 'warning' },
  matched: { label: 'Matched', tone: 'info' },
  applied: { label: 'Applied', tone: 'success' },
};
const remitStatus = (s: string) =>
  REMIT[s] ?? { label: s, tone: 'neutral' as BadgeTone };

/** "Remittances" (prototype payments(): record form beside recent remittances). */
export function Remittances() {
  const { hasPermission } = useSession();
  const remittances = useRemittances();
  return (
    <div className={dash.halves}>
      {hasPermission('remittances:manage') && <RecordRemittance />}
      <Card title="Recent remittances" flush>
        <DataTable
          caption="Recent remittances"
          rows={remittances.data}
          rowKey={(r) => r.id}
          loading={remittances.isLoading}
          error={remittances.error}
          onRetry={() => void remittances.refetch()}
          empty={
            <EmptyState
              title="No remittances"
              body="Record payment advice before knock-off."
            />
          }
          columns={[
            {
              key: 'r',
              header: 'Reference',
              render: (r) => <span className="mono">{r.reference}</span>,
            },
            { key: 'p', header: 'Panel', render: (r) => r.payer.name },
            { key: 'd', header: 'Date', render: (r) => dayLabel(r.receivedOn) },
            {
              key: 'a',
              header: 'Amount',
              align: 'right',
              render: (r) => formatRM(r.amountSen),
            },
            {
              key: 's',
              header: 'Status',
              render: (r) => {
                const s = remitStatus(r.status);
                return <Badge tone={s.tone}>{s.label}</Badge>;
              },
            },
          ]}
        />
      </Card>
    </div>
  );
}

function RecordRemittance() {
  const panels = usePanels();
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [payerId, setPayer] = useState('');
  const [receivedOn, setOn] = useState(todayKl());
  const [amount, setAmount] = useState('');
  const [reference, setRef] = useState('');
  const save = useMutation({
    mutationFn: (v: unknown) => api.post('/finance/remittances', v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance'] }),
  });
  const submit = () => {
    const sen = parseRM(amount);
    if (
      !payerId ||
      !receivedOn ||
      !sen ||
      sen < 1 ||
      reference.trim().length < 2
    )
      return toast.show({ tone: 'error', title: 'Complete the remittance' });
    save
      .mutateAsync({
        payerId,
        receivedOn,
        amountSen: sen,
        reference: reference.trim(),
      })
      .then(() => {
        toast.show({
          tone: 'success',
          title: 'Saved as unapplied',
          body: 'Match it in Knock-off.',
        });
        setAmount('');
        setRef('');
      })
      .catch(fail);
  };
  return (
    <Card title="Record a remittance">
      <div className={local.form}>
        <Field label="Panel">
          <Select
            value={payerId}
            placeholder="Choose…"
            options={(panels.data ?? []).map((p) => ({
              value: p.id,
              label: p.name,
            }))}
            onChange={(e) => setPayer(e.target.value)}
          />
        </Field>
        <Field label="Date received">
          <Input
            type="date"
            value={receivedOn}
            onChange={(e) => setOn(e.target.value)}
          />
        </Field>
        <Field label="Amount (RM)">
          <Input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Bank reference">
          <Input
            value={reference}
            maxLength={60}
            onChange={(e) => setRef(e.target.value)}
          />
        </Field>
        <Button
          variant="primary"
          block
          loading={save.isPending}
          onClick={submit}
        >
          Save as unapplied → knock-off
        </Button>
      </div>
    </Card>
  );
}

/** "Knock-off" (prototype knockoff(): unapplied payment, auto-match, allocation, decisions). */
export function KnockOff() {
  const { hasPermission } = useSession();
  const remittances = useRemittances();
  const actions = useFinanceActions();
  const toast = useToast();
  const fail = useFail();
  const open = (remittances.data ?? []).filter((r) => r.status !== 'applied');
  const [id, setId] = useState<string | null>(null);
  const r = open.find((x) => x.id === id) ?? open[0];
  const can = hasPermission('remittances:manage');
  // RL-1980: the API's unmatch needs claims:manage (like auto-match).
  const canUndo = hasPermission('claims:manage');
  if (!remittances.isLoading && !r)
    return (
      <Card title="Knock-off">
        <EmptyState
          title="Nothing to knock off"
          body="Unapplied remittances appear here once recorded."
        />
      </Card>
    );
  if (!r) return null;
  const allocated = r.allocations.reduce((s, a) => s + a.paidSen, 0);
  const deducted = r.allocations.reduce((s, a) => s + a.deductionSen, 0);
  return (
    <div className={dash.halves}>
      <Card
        title="Unapplied payment"
        actions={
          <>
            {open.length > 1 && (
              <Select
                aria-label="Remittance"
                className={local.inlineSelect}
                value={r.id}
                options={open.map((x) => ({
                  value: x.id,
                  label: `${x.payer.name} · ${x.reference}`,
                }))}
                onChange={(e) => setId(e.target.value)}
              />
            )}
            <Badge tone={remitStatus(r.status).tone}>
              {remitStatus(r.status).label}
            </Badge>
          </>
        }
      >
        <div className={local.form}>
          <dl className={local.kv}>
            <dt>Panel</dt>
            <dd>{r.payer.name}</dd>
            <dt>Received</dt>
            <dd>{dayLabel(r.receivedOn)}</dd>
            <dt>Reference</dt>
            <dd className="mono">{r.reference}</dd>
            <dt>Amount</dt>
            <dd>
              <strong>{formatRM(r.amountSen)}</strong>
            </dd>
          </dl>
          <p className={local.infoNote}>
            Auto-match by claim reference from the remittance advice, then by
            exact amount. Proposals are shown, not applied, until you confirm.
          </p>
          {can && (
            <Button
              block
              icon={<RefreshCw size={16} />}
              loading={actions.autoMatch.isPending}
              onClick={() =>
                void actions.autoMatch
                  .mutateAsync(r.id)
                  .then(() => toast.show({ tone: 'success', title: 'Matched' }))
                  .catch(fail)
              }
            >
              Run auto-match
            </Button>
          )}
        </div>
      </Card>
      <Card title="Allocation">
        {r.allocations.length ? (
          <div className={local.form}>
            <dl className={local.kv}>
              <dt>Allocated</dt>
              <dd>{formatRM(allocated)}</dd>
              <dt>Deductions</dt>
              <dd>{formatRM(deducted)}</dd>
              <dt>Received</dt>
              <dd>{formatRM(r.amountSen)}</dd>
              <dt>Unexplained gap</dt>
              <dd className={r.amountSen - allocated ? local.late : undefined}>
                {formatRM(r.amountSen - allocated)}
              </dd>
            </dl>
            {r.allocations
              .filter((a) => a.deductionSen > 0)
              .map((a) => (
                <DeductionRow
                  key={a.id}
                  allocation={a}
                  applied={r.status === 'applied'}
                  can={can}
                />
              ))}
            {can && (
              <Button
                variant="primary"
                block
                loading={actions.knockOff.isPending}
                onClick={() =>
                  void actions.knockOff
                    .mutateAsync(r.id)
                    .then(() =>
                      toast.show({ tone: 'success', title: 'Knocked off' }),
                    )
                    .catch(fail)
                }
              >
                Confirm knock-off
              </Button>
            )}
            {canUndo && <UndoMatch key={r.id} remittance={r} />}
          </div>
        ) : (
          <EmptyState
            title="Not matched yet"
            body="Run auto-match to propose an allocation."
          />
        )}
      </Card>
    </div>
  );
}

/**
 * RL-1980: "Undo match" before the knock-off, e.g. a payment matched from the
 * wrong branch. The matches are cleared (kept as history) and the claims are
 * free again for auto-match.
 */
function UndoMatch({ remittance: r }: { remittance: Remittance }) {
  const actions = useFinanceActions();
  const toast = useToast();
  const fail = useFail();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  // Stable, so the dialog does not move the focus on every keystroke.
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <Button block variant="ghost" onClick={() => setOpen(true)}>
        Undo match
      </Button>
      <Dialog
        open={open}
        onClose={close}
        variant="modal"
        title="Undo this match?"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Keep the match
            </Button>
            <Button
              variant="primary"
              loading={actions.unmatch.isPending}
              onClick={() =>
                void actions.unmatch
                  .mutateAsync({ id: r.id, reason: reason.trim() || undefined })
                  .then(() => {
                    setOpen(false);
                    toast.show({
                      tone: 'success',
                      title: 'Match undone',
                      body: 'The claims are free again. Run auto-match from the right branch.',
                    });
                  })
                  .catch(fail)
              }
            >
              Undo match
            </Button>
          </>
        }
      >
        <p>
          {`${r.payer.name} · ${r.reference}: the ${r.allocations.length} matched claim${r.allocations.length === 1 ? '' : 's'} and any deduction decisions are cleared. Nothing is paid until a knock-off is confirmed.`}
        </p>
        <Field label="Reason (optional)">
          <Input
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Matched from the wrong branch"
          />
        </Field>
      </Dialog>
    </>
  );
}

function DeductionRow({
  allocation: a,
  applied,
  can,
}: {
  allocation: Remittance['allocations'][number];
  /** Knocked off: the decision has been carried out. */
  applied: boolean;
  can: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fail = useFail();
  const [reason, setReason] = useState('');
  const decide = useMutation({
    mutationFn: (decision: string) =>
      api.post(`/finance/allocations/${a.id}/decision`, {
        decision,
        reason: reason.trim() || undefined,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance'] }),
  });
  return (
    <div className={local.warnNote}>
      <p>{`${formatRM(a.deductionSen)} deducted${a.deductionReason ? `: ${a.deductionReason}` : ''}.`}</p>
      {a.decision ? (
        <Badge tone="neutral">
          {deductionDecisionLabel(a.decision, applied)}
        </Badge>
      ) : (
        can && (
          <>
            <Input
              aria-label="Reason"
              placeholder="Reason (optional)"
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
            />
            <span className={local.acts}>
              {DEDUCTION_DECISIONS.map(({ value: k, label }) => (
                <Button
                  key={k}
                  size="sm"
                  variant={k === 'dispute' ? 'danger' : 'secondary'}
                  loading={decide.isPending}
                  onClick={() =>
                    void decide
                      .mutateAsync(k)
                      .then(() => toast.show({ tone: 'success', title: label }))
                      .catch(fail)
                  }
                >
                  {label}
                </Button>
              ))}
            </span>
          </>
        )
      )}
    </div>
  );
}

const MEMBER: Record<string, { label: string; tone: BadgeTone }> = {
  pending: { label: 'Pending', tone: 'warning' },
  approved: { label: 'Verified', tone: 'success' },
  rejected: { label: 'Not approved', tone: 'danger' },
};

/** "Member price verification" (prototype members(): finance-only, review card). */
export function Members() {
  const { hasPermission } = useSession();
  const q = useMemberVerifications('all');
  const [review, setReview] = useState<MemberVerification | null>(null);
  return (
    <>
      <Card
        title="Member price verification"
        flush
        actions={
          <span className={styles.sub}>
            Visible to finance only · sensitive under PDPA
          </span>
        }
      >
        <DataTable
          caption="Member verification"
          rows={q.data}
          rowKey={(v) => v.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          empty={
            <EmptyState
              title="No requests"
              body="Cards shown at the counter or uploaded in the app appear here."
            />
          }
          columns={[
            {
              key: 'r',
              header: 'Request',
              render: (v) => (
                <>
                  <span className="mono">
                    {v.source === 'app' ? 'App' : 'Counter'}
                  </span>
                  <p className={styles.sub}>{dayLabel(v.submittedAt)}</p>
                </>
              ),
            },
            {
              key: 'p',
              header: 'Patient',
              render: (v) => <strong>{v.patient.fullName}</strong>,
            },
            {
              key: 'c',
              header: 'Card',
              render: (v) => (
                <span className="mono">{`${v.cardType} ····${v.cardNoLast4}`}</span>
              ),
            },
            {
              key: 's',
              header: 'Status',
              render: (v) => {
                const m = MEMBER[v.status] ?? {
                  label: v.status,
                  tone: 'neutral' as BadgeTone,
                };
                return (
                  <Badge tone={m.tone}>
                    {v.status === 'approved' && v.validUntil
                      ? `${m.label} · to ${dayLabel(v.validUntil)}`
                      : m.label}
                  </Badge>
                );
              },
            },
            {
              key: 'a',
              header: <span className="visually-hidden">Actions</span>,
              align: 'right',
              render: (v) =>
                v.status === 'pending' &&
                hasPermission('member_verification:decide') ? (
                  <Button size="sm" onClick={() => setReview(v)}>
                    Review card
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>
      {review && <MemberReview v={review} onClose={() => setReview(null)} />}
    </>
  );
}

function MemberReview({
  v,
  onClose,
}: {
  v: MemberVerification;
  onClose: () => void;
}) {
  const actions = useFinanceActions();
  const toast = useToast();
  const fail = useFail();
  const [reason, setReason] = useState('');
  // RL-1760 (decision D23): an app request's card photo must be opened
  // before Approve.
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const urlRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );
  const needsPhoto = !!v.cardPhoto && !photoUrl;
  const openPhoto = () => {
    setPhotoLoading(true);
    api
      .get<Blob>(`/finance/member-verifications/${v.id}/card-photo`, {
        responseType: 'blob',
      })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        urlRef.current = url;
        setPhotoUrl(url);
      })
      .catch(fail)
      .finally(() => setPhotoLoading(false));
  };
  const go = (approve: boolean) => {
    if (approve && needsPhoto)
      return toast.show({
        tone: 'error',
        title: 'Open the card photo before approving',
      });
    if (!approve && !reason.trim())
      return toast.show({
        tone: 'error',
        title: 'The patient sees the reason; add one',
      });
    void actions.decideMember
      .mutateAsync({ id: v.id, approve, reason: reason.trim() || undefined })
      .then(() => {
        toast.show({
          tone: 'success',
          title: approve ? 'Member price approved' : 'Not approved',
        });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Review card · ${v.patient.fullName}`}
      footer={
        <>
          <Button
            variant="danger"
            loading={actions.decideMember.isPending}
            onClick={() => go(false)}
          >
            Not approved
          </Button>
          <Button
            variant="primary"
            loading={actions.decideMember.isPending}
            disabled={needsPhoto}
            onClick={() => go(true)}
          >
            Approve
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <dl className={local.kv}>
          <dt>Card</dt>
          <dd className="mono">{`${v.cardType} ····${v.cardNoLast4}`}</dd>
          {v.membershipNo && (
            <>
              <dt>Membership number</dt>
              <dd className="mono">{v.membershipNo}</dd>
            </>
          )}
          {v.email && (
            <>
              <dt>Email with the organisation</dt>
              <dd>{v.email}</dd>
            </>
          )}
          <dt>From</dt>
          <dd>{v.source === 'app' ? 'Patient app' : 'Counter'}</dd>
          <dt>Submitted</dt>
          <dd>{dayLabel(v.submittedAt)}</dd>
        </dl>
        {v.cardPhoto ? (
          photoUrl ? (
            <img className={local.cardPhoto} src={photoUrl} alt="Card photo" />
          ) : (
            <div className={local.acts}>
              <Button loading={photoLoading} onClick={openPhoto}>
                Open card photo
              </Button>
              <span className={styles.sub}>
                Check the photo before approving.
              </span>
            </div>
          )
        ) : (
          <p className={local.note}>
            Shown at the counter: the card was checked there (no photo).
          </p>
        )}
        <Field label="Reason (shown to the patient if not approved)">
          <Input
            value={reason}
            maxLength={300}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
